import type { Edl } from './edl';
import type { EditCommand } from './commands';
import { apply } from './reducer';

/**
 * Snapshot-based undo/redo.
 *
 * This is the primary undo mechanism (the guide allows "역커맨드 또는 스냅샷 전략").
 * Because the reducer is pure and the EDL is immutable, each snapshot is just a
 * reference to a frozen EDL value — no deep copy needed. A rejected command
 * (EditError) leaves history untouched and re-throws, so the stack never holds
 * an invalid state.
 */
export class History {
  private past: Edl[] = [];
  private future: Edl[] = [];
  private present: Edl;

  constructor(initial: Edl) {
    this.present = initial;
  }

  get current(): Edl {
    return this.present;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /**
   * Apply a command. On success, pushes the prior state onto the undo stack and
   * clears the redo stack. On EditError, nothing changes and the error re-throws.
   */
  dispatch(cmd: EditCommand): Edl {
    const next = apply(this.present, cmd); // throws before any mutation
    this.past.push(this.present);
    this.present = next;
    this.future = [];
    return this.present;
  }

  /**
   * 서버가 만든 상태로 갈아끼운다 (예: 저장 응답이 자막을 다시 깔아 돌려준 경우).
   *
   * 커맨드가 아니므로 되돌릴 수 있게 undo 스택에 현재 상태를 남긴다 — 사용자가
   * "방금 저장 전으로" 돌아갈 수 있어야 한다. redo 스택은 무효가 되므로 비운다.
   */
  replace(next: Edl): Edl {
    if (next === this.present) return this.present;
    this.past.push(this.present);
    this.present = next;
    this.future = [];
    return this.present;
  }

  undo(): Edl {
    const prev = this.past.pop();
    if (prev === undefined) return this.present;
    this.future.push(this.present);
    this.present = prev;
    return this.present;
  }

  redo(): Edl {
    const next = this.future.pop();
    if (next === undefined) return this.present;
    this.past.push(this.present);
    this.present = next;
    return this.present;
  }
}
